/**
 * Ticket mesajlarına iliştirilen yapılandırılmış bağlam blokları.
 *
 * Backend'de mesaj başına serbest bir metadata alanı olmadığı için bağlam,
 * gövdenin sonuna insan tarafından da okunabilir bloklar hâlinde eklenir.
 * Böylece destek ekibinin aldığı e-postada bilgi düz metin olarak görünür,
 * arayüzde ise aynı blok ayrıştırılıp zengin kart olarak gösterilir.
 *
 * Backend ileride `metadata` alanı eklerse yalnızca build/parse fonksiyonları
 * değişir, çağıran bileşenler aynı kalır.
 */

import type { DiagnosticsInfo } from "./diagnostics";

export interface GenerationReference {
  id: string;
  title: string;
  type: string;
  /** R2 yolu — gösterim için getFileUrl'den geçirilir */
  fileUrl: string;
  prompt?: string;
}

export interface TicketMessageContext {
  diagnostics?: DiagnosticsInfo;
  generation?: GenerationReference;
}

export interface ParsedMessageBody extends TicketMessageContext {
  /** Kullanıcının yazdığı düz metin */
  text: string;
}

const DIAGNOSTICS_MARKER = "--- Diagnostics ---";
const GENERATION_MARKER = "--- Generation ---";

/** Blok başlangıçlarını yakalar; bölme sırasında ayıraçlar da korunur */
const BLOCK_SPLIT_REGEX = /^(--- (?:Diagnostics|Generation) ---)$/m;

/** `Key: Value` satırlarını okur; `lastKey` verilirse kalan tüm metni ona atar */
const parseFields = (
  block: string,
  lastKey?: string,
): Record<string, string> => {
  const fields: Record<string, string> = {};
  const lines = block.split(/\r?\n/);

  for (let index = 0; index < lines.length; index += 1) {
    const match = lines[index].match(/^([A-Za-z ]+):\s*(.*)$/);
    if (!match) continue;

    const key = match[1].trim().toLowerCase();
    // Çok satırlı olabilecek son alan (ör. prompt) bloğun sonuna kadar okunur
    if (lastKey && key === lastKey) {
      fields[key] = [match[2], ...lines.slice(index + 1)].join("\n").trim();
      break;
    }
    fields[key] = match[2].trim();
  }

  return fields;
};

/** Eski kayıtlarda gövdenin başına gömülmüş `[Model: ...]` satırlarını ayıklar */
const stripLegacyMetadata = (text: string): string => {
  const lines = text.split(/\r?\n/);
  let index = 0;
  while (index < lines.length && /^\[[^\]]+\]$/.test(lines[index].trim())) {
    index += 1;
  }
  return lines.slice(index).join("\n").trim() || text.trim();
};

/**
 * Kullanıcı metni ve seçili bağlamı tek bir gövde metnine dönüştürür.
 */
export const buildMessageBody = (
  text: string,
  context: TicketMessageContext = {},
): string => {
  const parts = [text.trim()];
  const { diagnostics, generation } = context;

  if (diagnostics) {
    const rows = [
      `Browser: ${diagnostics.browser}`,
      `OS: ${diagnostics.os}`,
      `Screen: ${diagnostics.screen}`,
      `Viewport: ${diagnostics.viewport}`,
      `Timezone: ${diagnostics.timezone}`,
      `Language: ${diagnostics.language}`,
    ];
    if (diagnostics.page) rows.push(`Page: ${diagnostics.page}`);
    if (diagnostics.build) rows.push(`Build: ${diagnostics.build}`);
    parts.push([DIAGNOSTICS_MARKER, ...rows].join("\n"));
  }

  if (generation) {
    const rows = [
      `ID: ${generation.id}`,
      `Title: ${generation.title}`,
      `Type: ${generation.type}`,
      `File: ${generation.fileUrl}`,
    ];
    // Prompt çok satırlı olabildiği için her zaman son sırada durur
    if (generation.prompt) rows.push(`Prompt: ${generation.prompt}`);
    parts.push([GENERATION_MARKER, ...rows].join("\n"));
  }

  return parts.filter(Boolean).join("\n\n");
};

/**
 * Gövdeyi kullanıcı metni ve bağlam bloklarına ayırır.
 */
export const parseMessageBody = (body: string): ParsedMessageBody => {
  const segments = body.split(BLOCK_SPLIT_REGEX);
  const result: ParsedMessageBody = {
    text: stripLegacyMetadata(segments[0] ?? ""),
  };

  for (let index = 1; index < segments.length; index += 2) {
    const marker = segments[index];
    const block = segments[index + 1] ?? "";

    if (marker === DIAGNOSTICS_MARKER) {
      const fields = parseFields(block);
      result.diagnostics = {
        browser: fields.browser ?? "",
        os: fields.os ?? "",
        screen: fields.screen ?? "",
        viewport: fields.viewport ?? "",
        timezone: fields.timezone ?? "",
        language: fields.language ?? "",
        page: fields.page,
        build: fields.build,
      };
    }

    if (marker === GENERATION_MARKER) {
      const fields = parseFields(block, "prompt");
      if (fields.id && fields.file) {
        result.generation = {
          id: fields.id,
          title: fields.title ?? "",
          type: fields.type ?? "image",
          fileUrl: fields.file,
          prompt: fields.prompt,
        };
      }
    }
  }

  return result;
};
